import React, { useState, useContext, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import { CategoriasProvider } from './context/CategoriasContext';
import { AuthModalProvider } from './context/AuthModalContext';
import Navbar from './components/Formularios/Header/Navbar/Navbar';
import Principal from './pages/Principal/Principal';
import De from './pages/De/De';
import SignIn from './pages/SignIn/SignIn';
import SignUp from './pages/SignUp/SignUp';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import Profile from './pages/Profile/Profile';
import Settings from './pages/Settings/Settings';
import NotFound from './pages/NotFound/NotFound';
import MiTrastero from './pages/MiTrastero/MiTrastero';
//import OpsLogin from './pages/OpsLogin/OpsLogin';
//import OpsDashboard from './pages/OpsDashboard/OpsDashboard';
//import OpsSistemaDisco from './pages/OpsSistemaDisco/OpsSistemaDisco.jsx';//

import LegalPage from './pages/Legal/LegalPage';
import ComoFunciona from './pages/ComoFunciona/ComoFunciona';
import FAQ from './pages/FAQ/FAQ';
import Consejos from './pages/Consejos/Consejos';
import Denunciar from './pages/Denunciar/Denunciar';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import VerificarEmail from './pages/VerificarEmail/VerificarEmail';
import Footer from './components/Footer/Footer';
import CookieBanner from './components/CookieBanner/CookieBanner';
import ConsentWall from './components/ConsentWall/ConsentWall';
import useCardScale from './hooks/useCardScale';
import { buildBackendImgUrl } from './utils/api';
import { AppContainer, ContentWrapper } from './App.styles';
import { PageProvider } from './context/PageContext';
import { useAuthModal } from './context/AuthModalContext';
const Mensajes     = lazy(() => import('./pages/Mensajes/Mensajes'));
const ModalLogin   = lazy(() => import('./components/Modal/ModalLogin/ModalLogin'));
const ContactDenuncia = lazy(() => import('./components/Content/ContactContent'));
const SignInContent          = lazy(() => import('./components/Auth/SignInContent'));
const SignUpContent          = lazy(() => import('./components/Auth/SignUpContent'));
const ForgotPasswordContent  = lazy(() => import('./components/Auth/ForgotPasswordContent'));

function ConsentWallGuard({ user, onAccepted }) {
  const location = useLocation();
  if (location.pathname.startsWith('/legal/')) return null;
  return <ConsentWall user={user} onAccepted={onAccepted} />;
}

function AppContent() { 
  useCardScale();
  const [mensajesOpen,  setMensajesOpen]  = useState(false);
  const [denunciarOpen, setDenunciarOpen] = useState(false);
  const { isOpen: authOpen, mode: authMode, setMode: setAuthMode, closeAuthModal } = useAuthModal();

  useEffect(() => {
    document.body.style.overflow = mensajesOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mensajesOpen]);
  const [user, setUser] = useState(() => {
    try {
      // Restaurar sesión desde localStorage al recargar
      const stored = localStorage.getItem('user');
      if (!stored) return null;

      // Verificar que el token JWT no ha expirado (decodificación sin librería)
      const token = localStorage.getItem('trastero_token');
      if (token) {
        const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(b64.padEnd(b64.length + (4 - b64.length % 4) % 4, '=')));
        if (payload.exp && Date.now() / 1000 > payload.exp) {
          // Token expirado — limpiar sesión
          localStorage.removeItem('user');
          localStorage.removeItem('trastero_token');
          return null;
        }
      }

      return JSON.parse(stored);
    } catch {
      return null;
    }
  });
  const { theme } = useContext(ThemeContext);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('trastero_token');
  };
  
  const navigate = useNavigate();
  const handleAuthSuccess = (userData) => {
    handleLogin(userData);
    closeAuthModal();
    navigate('/');
  };
  return (
    
      <PageProvider>
      <AppContainer bgColor={theme.background}>
        <Navbar user={user} onLogout={handleLogout} onLogin={handleLogin} onToggleMensajes={() => setMensajesOpen(v => !v)} />
        <ContentWrapper bgColor={theme.background} textColor={theme.text}>
          <Routes>
            <Route path="/" element={<Principal />} />
            <Route path="/de/:nombre" element={<De user={user} />} />
            <Route path="/signin" element={<SignIn onLogin={handleLogin} />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/mi-perfil" element={<Profile user={user} onLogout={handleLogout} onUserUpdate={u => {
              // Siempre sanitizamos el avatar antes de guardar en estado y localStorage,
              // para evitar que quede guardado con el dominio del frontend en vez del backend.
              const sanitized = { ...u, avatar: buildBackendImgUrl(u.avatar) || u.avatar };
              setUser(sanitized);
              localStorage.setItem('user', JSON.stringify(sanitized));
            }} />} />
            <Route path="/mi-trastero" element={<MiTrastero user={user} />} />
            <Route path="/settings" element={<Settings />} />
            {/* Rutas de sistema — sin enlace en navbar */}
           {/* <Route path="/ops/login"     element={<OpsLogin />} />
               <Route path="/ops/dashboard" element={<OpsDashboard />} />*/}
            {/* Sistema/Disco — solo en development */}
    {/*/      {process.env.NODE_ENV === 'development' && OpsSistemaDisco && (
              <Route path="/ops/sistema" element={<OpsSistemaDisco />} />
            )}            */}
            <Route path="/legal/:doc"    element={<LegalPage />} />
            <Route path="/como-funciona" element={<ComoFunciona />} />
            <Route path="/faq"           element={<FAQ />} />
            <Route path="/consejos"      element={<Consejos />} />
            <Route path="/denunciar"              element={<Denunciar />} />
            <Route path="/reset-password/:token"  element={<ResetPassword />} />
            <Route path="/verificar-email"        element={<VerificarEmail />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ContentWrapper>
        <Footer onDenunciar={() => setDenunciarOpen(true)} />
        {denunciarOpen && (
          <Suspense fallback={null}>
            <ModalLogin isOpen={denunciarOpen} onClose={() => setDenunciarOpen(false)}>
              <ContactDenuncia tipo="denuncia" onClose={() => setDenunciarOpen(false)} />
            </ModalLogin>
          </Suspense>
        )}
        {mensajesOpen && (
          <Suspense fallback={null}>
            <Mensajes user={user} onClose={() => setMensajesOpen(false)} />
          </Suspense>
        )}
        {authOpen && (
          <Suspense fallback={null}>
            <ModalLogin isOpen={authOpen} onClose={closeAuthModal}>
              {authMode === 'signin' ? (
                <SignInContent onLogin={handleAuthSuccess} onSwitchToSignUp={() => setAuthMode('signup')} onSwitchToForgot={() => setAuthMode('forgot')} />
              ) : authMode === 'signup' ? (
                <SignUpContent onSignUp={handleAuthSuccess} onSwitchToSignIn={() => setAuthMode('signin')} />
              ) : (
                <ForgotPasswordContent onSwitchToSignIn={() => setAuthMode('signin')} />
              )}
            </ModalLogin>
          </Suspense>
        )}        
        <CookieBanner />
        {user && (!user.acepta_terminos || !user.acepta_privacidad) && (
          <ConsentWallGuard
            user={user}
            onAccepted={() => {
              const updated = { ...user, acepta_terminos: true, acepta_privacidad: true };
              setUser(updated);
              localStorage.setItem('user', JSON.stringify(updated));
            }}
          />
        )}
      </AppContainer>
      </PageProvider>

  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <CategoriasProvider>
          <AuthModalProvider>
            <Router>
              <AppContent />
            </Router>
          </AuthModalProvider>
        </CategoriasProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
